<template>
    <main class="permissions-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">{{ $t('menu.companies.permissions') }}</div>
        </portal>

        <header class="permissions-hero">
            <h1 class="permissions-hero-title">{{ $t('menu.companies.permissions') }}</h1>
            <p class="permissions-hero-sub">{{ $t('menu.companies.permissions_subtitle') }}</p>
        </header>

        <section class="permissions-card">
            <div v-if="isLoading" class="permissions-state">
                <BmvLoader :active="true" />
            </div>

            <div v-else-if="loadError" class="permissions-state permissions-state--error">
                <i class="fal fa-exclamation-triangle"></i>
                <p>{{ $t('permissions.load_error') }}</p>
                <button class="permissions-retry-btn" @click="loadData">
                    {{ $t('permissions.retry') }}
                </button>
            </div>

            <PermissionsTable
                v-else
                :roles="roles"
                :defaults="defaults"
                @save="handleSave"
            />
        </section>
    </main>
</template>

<script>
import PermissionsTable from '@/components/Model/Company/PermissionsTable'
import BmvLoader from '@/components/UI/v2/BmvLoader.vue'
import { mapActions, mapState } from 'vuex'

export default {
    name: 'Permissions',
    components: {
        PermissionsTable,
        BmvLoader
    },
    data() {
        return {
            isLoading: true,
            loadError: false
        }
    },
    computed: {
        ...mapState('permission', {
            allRoles: (state) => state.roles,
            defaults: (state) => state.defaults
        }),
        ...mapState('auth', {
            currentRole: (state) => state.role
        }),
        isSuperAdmin() {
            const name = this.currentRole && typeof this.currentRole === 'object'
                ? this.currentRole.name
                : this.currentRole
            return name === 'super-admin'
        },
        roles() {
            if (this.isSuperAdmin) return this.allRoles
            return this.allRoles.filter((r) => r.name !== 'super-admin')
        }
    },
    created() {
        this.loadData()
    },
    methods: {
        ...mapActions('permission', [
            'getRoles',
            'getDefaults',
            'updateBulkPermissions'
        ]),
        async loadData() {
            this.isLoading = true
            this.loadError = false
            try {
                await Promise.all([this.getRoles(), this.getDefaults()])
                this.isLoading = false
            } catch (error) {
                this.isLoading = false
                this.loadError = true
                this.$store.dispatch('dispatchError', error)
            }
        },
        handleSave({ roles, callback }) {
            this.updateBulkPermissions(roles)
                .then(() => {
                    this.getRoles()
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('permissions.save_success'),
                        message: ''
                    })
                    callback()
                })
                .catch((error) => {
                    this.$store.dispatch('dispatchError', error)
                    callback()
                })
        }
    }
}
</script>

<style scoped>
.permissions-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

.permissions-hero {
    margin-bottom: var(--v2-space-5);
}

.permissions-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.permissions-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
    max-width: 820px;
}

.permissions-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
    position: relative;
    min-height: 240px;
}

.permissions-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--v2-space-3);
    padding: var(--v2-space-6) var(--v2-space-5);
    text-align: center;
    color: var(--v2-text-3);
}

.permissions-state--error i {
    font-size: 1.75rem;
    color: var(--v2-warning);
}

.permissions-state p {
    margin: 0;
    font-size: var(--v2-text-sm-size);
}

.permissions-retry-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 14px;
    border-radius: var(--v2-radius-md);
    background: var(--v2-primary);
    color: #fff;
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-semibold);
    border: none;
    cursor: pointer;
    transition: background var(--v2-transition-fast);
}

.permissions-retry-btn:hover {
    background: var(--v2-primary-hover, #0e7490);
}

@media (max-width: 900px) {
    .permissions-v2 {
        padding: 16px 18px 32px;
    }

    .permissions-hero-title {
        font-size: 1.375rem;
    }
}
</style>
